import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import GooglePlacesAutocompleteInput from '../../components/common/GooglePlacesAutocompleteInput';
import { addressFieldsFromPlaceDetails } from '../../utils/googlePlaces';
import { uploadFile } from '../../services/fileUploadService';
import { patchProfileFields, submitIdentityVerification, submitWorkAuthorization } from '../../services/profileService';
import { startEmailVerification, startPhoneVerification, verifyPhone } from '../../services/verificationService';
import { digitsOnly, formatSSN, isValidSSN, maskSSN } from '../../utils/profileFieldMapping';
import '../../styles/ProfileDetails.css';

type UploadedDocument = {
  fileId: string;
  name: string;
  previewUrl?: string;
  mimeType?: string;
  uploadedAt: string;
};

const documentPageLabel = (index: number) => {
  if (index === 0) return 'Front / First Page';
  if (index === 1) return 'Back / Second Page';
  return `Additional Page ${index + 1}`;
};

const displayableMediaUrl = (...values: Array<unknown>) =>
  values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => /^(https?:|blob:|data:image\/)/.test(value)) || '';

const existingDocumentRefs = (ids?: string[]): UploadedDocument[] =>
  (ids || []).filter(Boolean).map((fileId, index) => ({
    fileId,
    name: `Uploaded document ${index + 1}`,
    previewUrl: displayableMediaUrl(fileId),
    uploadedAt: '',
  }));

const stringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const formatDateTime = (value: unknown) => {
  if (!value) return '';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const isVerificationApproved = (verification: any, fallback?: any) =>
  verification?.isVerified === true ||
  verification?.verified === true ||
  fallback?.isVerified === true ||
  fallback?.verified === true ||
  String(verification?.verificationDecision || '').toUpperCase() === 'APPROVED' ||
  String(fallback?.verificationDecision || '').toUpperCase() === 'APPROVED';

const callingCodes = ['+1', '+44', '+61', '+254', '+91'];
const unrestrictedWorkAuthorizationTypes = ['US_CITIZEN', 'PERMANENT_RESIDENT'];
const manualReviewWorkAuthorizationTypes = ['INTERNATIONAL_STUDENT', 'WORK_VISA', 'DEPENDENT_VISA_WITH_WORK_AUTH'];

const splitPhone = (phone?: string | null) => {
  const raw = (phone || '').trim();
  const normalized = raw.startsWith('+') ? raw : raw ? `+1${digitsOnly(raw)}` : '';
  const code = callingCodes.find((item) => normalized.startsWith(item)) || '+1';
  return { code, national: digitsOnly(normalized.slice(code.length)) };
};

const ProfileDetails = () => {
  const { user, updateProfile, uploadProfilePicture, refreshAuthState, refreshProfile, getAccessToken } = useAuth();
  const { googleMapsApiKey } = useAppConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [phoneCallingCode, setPhoneCallingCode] = useState('+1');
  const [phoneNational, setPhoneNational] = useState('');
  
  // Expand/Collapse sections
  const [expandedSections, setExpandedSections] = useState({
    bioData: false,
    address: false,
    identityVerification: false,
    workAuthorization: false,
  });
  
  // Form data
  const [profileData, setProfileData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    ssn: '',
    dateOfBirth: '',
    gender: '',
    otherGender: '',
    status: 'ACTIVE',
  });
  
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    county: '',
    latitude: '',
    longitude: '',
    placeId: '',
  });
  
  const [identityDocument, setIdentityDocument] = useState({
    documentType: '',
    uploadedOn: '',
    documentUrl: '',
    idDocumentIds: [] as string[],
    status: 'not_uploaded',
  });
  const [workAuthorization, setWorkAuthorization] = useState({
    workAuthorizationType: '',
    expirationDate: '',
    notes: '',
    attestedAuthorized: false,
    idDocumentIds: [] as string[],
    status: 'not_uploaded',
  });
  
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [securityPhoto, setSecurityPhoto] = useState<File | null>(null);
  const [identityDocuments, setIdentityDocuments] = useState<UploadedDocument[]>([]);
  const [workAuthorizationDocuments, setWorkAuthorizationDocuments] = useState<UploadedDocument[]>([]);
  const [identityReplaceIndex, setIdentityReplaceIndex] = useState<number | null>(null);
  const [workReplaceIndex, setWorkReplaceIndex] = useState<number | null>(null);
  const [isEditingSSN, setIsEditingSSN] = useState(false);
  const [ssnEditValue, setSsnEditValue] = useState('');
  const [phoneOtpToken, setPhoneOtpToken] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [isIdentityCameraOpen, setIsIdentityCameraOpen] = useState(false);
  const [identityCameraStream, setIdentityCameraStream] = useState<MediaStream | null>(null);
  const [identityCameraError, setIdentityCameraError] = useState('');

  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const securityPhotoInputRef = useRef<HTMLInputElement>(null);
  const identityDocumentInputRef = useRef<HTMLInputElement>(null);
  const identityCameraVideoRef = useRef<HTMLVideoElement>(null);
  const identityCameraStreamRef = useRef<MediaStream | null>(null);
  const identityDocumentReplaceInputRef = useRef<HTMLInputElement>(null);
  const workAuthorizationInputRef = useRef<HTMLInputElement>(null);
  const workAuthorizationReplaceInputRef = useRef<HTMLInputElement>(null);
  const identityReplaceIndexRef = useRef<number | null>(null);
  const workReplaceIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        middleName: user.middleName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        ssn: '',
        dateOfBirth: (user as any).birthDate || (user as any).dateOfBirth || '',
        gender: (user as any).gender || '',
        otherGender: (user as any).otherGender || '',
        status: (user as any).status || 'ACTIVE',
      });
      const phoneParts = splitPhone(user.phone);
      setPhoneCallingCode(phoneParts.code);
      setPhoneNational(phoneParts.national);
      
      if ((user as any).permanentAddress || (user as any).address) {
        setAddress((prev) => ({ ...prev, ...((user as any).permanentAddress || (user as any).address) }));
      }
      
      if ((user as any).userIdentityVerification || (user as any).identityDocument) {
        const verification = (user as any).userIdentityVerification || {};
        const legacyDocument = (user as any).identityDocument || {};
        const idDocumentIds = stringArray(verification.idDocumentIds || legacyDocument.idDocumentIds);
        const idDocumentUrls = stringArray(verification.idDocumentUrls || legacyDocument.idDocumentUrls || legacyDocument.documentUrl);
        const documentRefs = idDocumentIds.length ? idDocumentIds : idDocumentUrls;
        setIdentityDocuments(existingDocumentRefs(documentRefs));
        setIdentityDocument({
          documentType: verification.identificationDocumentType || legacyDocument.documentType || '',
          uploadedOn: verification.uploadedOn || legacyDocument.uploadedOn || '',
          documentUrl: idDocumentUrls[0] || legacyDocument.documentUrl || '',
          idDocumentIds,
          status:
            isVerificationApproved(verification, legacyDocument)
              ? 'verified'
              : legacyDocument.status || verification.id || idDocumentIds.length || idDocumentUrls.length
                ? 'pending'
                : 'not_uploaded',
        });
      }

      if ((user as any).userWorkAuthorization) {
        const workAuth = (user as any).userWorkAuthorization;
        const idDocumentIds = stringArray(workAuth.idDocumentIds);
        setWorkAuthorizationDocuments(existingDocumentRefs(idDocumentIds));
        setWorkAuthorization({
          workAuthorizationType: workAuth.workAuthorizationType || workAuth.type || '',
          expirationDate: workAuth.expirationDate || '',
          notes: workAuth.notes || '',
          attestedAuthorized: false,
          idDocumentIds,
          status: isVerificationApproved(workAuth) ? 'verified' : workAuth.id ? 'pending' : 'not_uploaded',
        });
      }
    }
  }, [user]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressSelected = ({ details }: { details: any }) => {
    const next = addressFieldsFromPlaceDetails(details);
    setAddress((prev) => ({
      ...prev,
      street: next.street,
      city: next.city,
      state: next.state,
      zipCode: next.zipCode,
      country: next.country || prev.country,
      county: next.county,
      latitude: next.latitude !== undefined ? String(next.latitude) : '',
      longitude: next.longitude !== undefined ? String(next.longitude) : '',
      placeId: next.placeId,
    }));
  };

  const openProfilePhotoPicker = (input: HTMLInputElement | null) => {
    if (!input || isLoading) return;
    input.value = '';
    input.click();
  };

  const openIdentityDocumentPicker = () => {
    const input = identityDocumentInputRef.current;
    if (!input || isLoading) return;

    input.value = '';
    input.click();
  };

  const stopIdentityDocumentCamera = () => {
    identityCameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    identityCameraStreamRef.current = null;
    setIdentityCameraStream(null);
    setIdentityCameraError('');
    setIsIdentityCameraOpen(false);
  };

  const openIdentityDocumentCamera = async () => {
    if (isLoading) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      setMessage({ type: 'error', text: 'Camera is not available in this browser. Use Gallery to upload the ID image.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setIdentityCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      identityCameraStreamRef.current = stream;
      setIdentityCameraStream(stream);
      setIsIdentityCameraOpen(true);
    } catch (error: any) {
      setIdentityCameraError(error?.message || 'Camera access was blocked. Please allow camera access and try again.');
      setIsIdentityCameraOpen(true);
    }
  };

  const captureIdentityDocumentPhoto = async () => {
    const video = identityCameraVideoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setIdentityCameraError('Camera preview is still loading. Please try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      setIdentityCameraError('Unable to capture a photo from this camera.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      setIdentityCameraError('Unable to save the captured photo.');
      return;
    }

    const file = new File([blob], `identity-document-${Date.now()}.jpg`, { type: 'image/jpeg' });
    stopIdentityDocumentCamera();
    await handleIdentityDocumentUpload([file]);
  };

  const openIdentityDocumentReplacePicker = (index: number) => {
    const input = identityDocumentReplaceInputRef.current;
    if (!input || isLoading) return;

    identityReplaceIndexRef.current = index;
    setIdentityReplaceIndex(index);
    input.value = '';
    input.accept = 'image/*,.pdf';
    input.removeAttribute('capture');
    input.click();
  };

  const openWorkAuthorizationPicker = () => {
    const input = workAuthorizationInputRef.current;
    if (!input || isLoading) return;

    input.value = '';
    input.click();
  };

  const openWorkAuthorizationReplacePicker = (index: number) => {
    const input = workAuthorizationReplaceInputRef.current;
    if (!input || isLoading) return;

    workReplaceIndexRef.current = index;
    setWorkReplaceIndex(index);
    input.value = '';
    input.click();
  };

  useEffect(() => {
    const video = identityCameraVideoRef.current;
    if (!isIdentityCameraOpen || !identityCameraStream || !video) return;

    video.srcObject = identityCameraStream;
    video.play().catch(() => {
      setIdentityCameraError('Unable to start camera preview.');
    });

    return () => {
      video.srcObject = null;
    };
  }, [isIdentityCameraOpen, identityCameraStream]);

  useEffect(() => () => {
    identityCameraStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const handlePhotoUpload = async (type: 'profile' | 'security', file: File) => {
    setMessage(null);
    setIsLoading(true);
    try {
      if (type === 'profile') {
        setProfilePhoto(file);
        await uploadProfilePicture(file);
        await refreshProfile();
        setMessage({ type: 'success', text: 'Profile photo uploaded!' });
      } else {
        if (!user?.id) {
          setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
          return;
        }
        setSecurityPhoto(file);
        const result = await uploadFile(user.id, file, { purpose: 'USER_PROFILE', fileType: 'PHOTO' });
        await patchProfileFields([{ name: 'securityPhoto', value: result.fileId }]);
        await refreshProfile();
        setMessage({ type: 'success', text: 'Security photo uploaded!' });
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to upload photo.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const uploadDocumentFiles = async (files: FileList | File[]): Promise<UploadedDocument[]> => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
      return [];
    }

    const uploadedDocuments: UploadedDocument[] = [];
    for (const file of Array.from(files)) {
      const result = await uploadFile(user.id, file, {
        purpose: 'USER_PROFILE',
        fileType: file.type.startsWith('image/') ? 'PHOTO' : 'DOCUMENT',
      });
      uploadedDocuments.push({
        fileId: result.fileId,
        name: file.name,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        mimeType: file.type,
        uploadedAt: new Date().toLocaleString(),
      });
    }
    return uploadedDocuments;
  };

  const handleIdentityDocumentUpload = async (files: FileList | File[], replaceIndex?: number | null) => {
    setMessage(null);
    setIsLoading(true);
    try {
      const uploadedDocuments = await uploadDocumentFiles(files);
      if (!uploadedDocuments.length) return;
      const nextDocuments =
        typeof replaceIndex === 'number'
          ? identityDocuments.map((doc, index) => (index === replaceIndex ? uploadedDocuments[0] : doc))
          : [...identityDocuments, ...uploadedDocuments];
      const uploadedIds = nextDocuments.map((doc) => doc.fileId);

      setIdentityDocuments(nextDocuments);
      setIdentityDocument((prev) => ({
        ...prev,
        uploadedOn: uploadedDocuments[0]?.uploadedAt || prev.uploadedOn,
        documentUrl: nextDocuments[0]?.previewUrl || prev.documentUrl,
        idDocumentIds: uploadedIds,
        status: 'uploaded',
      }));
      setMessage({ type: 'success', text: `${uploadedDocuments.length} document file(s) uploaded.` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to upload identity document.' });
    } finally {
      setIsLoading(false);
      setIdentityReplaceIndex(null);
      identityReplaceIndexRef.current = null;
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const removeIdentityDocument = (indexToRemove: number) => {
    const nextDocuments = identityDocuments.filter((_, index) => index !== indexToRemove);
    setIdentityDocuments(nextDocuments);
    setIdentityDocument((prev) => ({
      ...prev,
      documentUrl: nextDocuments[0]?.previewUrl || '',
      idDocumentIds: nextDocuments.map((doc) => doc.fileId),
      status: nextDocuments.length ? 'uploaded' : 'not_uploaded',
    }));
  };

  const handlePhonePartChange = (code: string, national: string) => {
    const cleaned = digitsOnly(national);
    setPhoneCallingCode(code);
    setPhoneNational(cleaned);
    setProfileData((prev) => ({ ...prev, phone: cleaned ? `${code}${cleaned}` : '' }));
  };

  const identityNextPageLabel = () => {
    if (identityDocuments.length === 0) return 'Front / First Page';
    if (identityDocuments.length === 1) return 'Back / Second Page';
    return `Additional Page ${identityDocuments.length + 1}`;
  };

  const saveSsn = async () => {
    const clean = digitsOnly(ssnEditValue);
    if (!isValidSSN(clean)) {
      setMessage({ type: 'error', text: 'Please enter a valid 9-digit SSN.' });
      return;
    }

    setIsLoading(true);
    try {
      await patchProfileFields([{ name: 'ssn', value: clean }]);
      await refreshProfile();
      setIsEditingSSN(false);
      setSsnEditValue('');
      setMessage({ type: 'success', text: 'SSN updated successfully.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to update SSN.' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleSendPhoneVerification = async () => {
    if (digitsOnly(phoneNational).length < 7) {
      setMessage({ type: 'error', text: 'Enter a valid phone number with at least 7 digits.' });
      return;
    }

    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const response = await startPhoneVerification(token, profileData.phone || user?.phone);
      setPhoneOtpToken(response.otpToken || '');
      setPhoneVerificationCode('');
      setShowPhoneVerification(true);
      setMessage({ type: 'success', text: 'Verification code sent to your phone.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to send verification code.' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneOtpToken || !phoneVerificationCode.trim()) {
      setMessage({ type: 'error', text: 'Enter the verification code sent to your phone.' });
      return;
    }

    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      await verifyPhone(phoneOtpToken, phoneVerificationCode.trim(), token);
      await refreshProfile();
      setShowPhoneVerification(false);
      setPhoneVerificationCode('');
      setPhoneOtpToken('');
      setMessage({ type: 'success', text: 'Phone number verified successfully.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Invalid verification code.' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleSubmitIdentityVerification = async () => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
      return;
    }
    if (!identityDocument.documentType) {
      setMessage({ type: 'error', text: 'Select a document type before submitting.' });
      return;
    }
    if (identityDocument.idDocumentIds.length < 2) {
      setMessage({ type: 'error', text: 'Please upload at least the front and back of your identity document.' });
      return;
    }

    setIsLoading(true);
    try {
      await submitIdentityVerification(user.id, {
        documentType: identityDocument.documentType,
        identificationDocumentType: identityDocument.documentType,
        idDocumentIds: identityDocument.idDocumentIds,
      });
      await refreshProfile();
      setIdentityDocument((prev) => ({ ...prev, status: 'pending' }));
      setMessage({ type: 'success', text: 'Identity verification submitted.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to submit identity verification.' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleWorkAuthorizationUpload = async (files: FileList | File[], replaceIndex?: number | null) => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
      return;
    }

    setMessage(null);
    setIsLoading(true);
    try {
      const uploadedDocuments = await uploadDocumentFiles(files);
      if (!uploadedDocuments.length) return;
      const nextDocuments =
        typeof replaceIndex === 'number'
          ? workAuthorizationDocuments.map((doc, index) => (index === replaceIndex ? uploadedDocuments[0] : doc))
          : [...workAuthorizationDocuments, ...uploadedDocuments];

      setWorkAuthorizationDocuments(nextDocuments);
      setWorkAuthorization((prev) => ({
        ...prev,
        idDocumentIds: nextDocuments.map((doc) => doc.fileId),
        status: 'uploaded',
      }));
      setMessage({ type: 'success', text: `${uploadedDocuments.length} work authorization document(s) uploaded.` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to upload work authorization documents.' });
    } finally {
      setIsLoading(false);
      setWorkReplaceIndex(null);
      workReplaceIndexRef.current = null;
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const removeWorkAuthorizationDocument = (indexToRemove: number) => {
    const nextDocuments = workAuthorizationDocuments.filter((_, index) => index !== indexToRemove);
    setWorkAuthorizationDocuments(nextDocuments);
    setWorkAuthorization((prev) => ({
      ...prev,
      idDocumentIds: nextDocuments.map((doc) => doc.fileId),
      status: nextDocuments.length ? 'uploaded' : 'not_uploaded',
    }));
  };

  const handleSubmitWorkAuthorization = async () => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
      return;
    }
    const workAuthorizationType = workAuthorization.workAuthorizationType;
    if (!workAuthorizationType) {
      setMessage({ type: 'error', text: 'Select a work authorization type before submitting.' });
      return;
    }
    if (!workAuthorization.attestedAuthorized) {
      setMessage({ type: 'error', text: 'Please confirm you are authorized to perform work through Olitask before submitting.' });
      return;
    }
    if (workAuthorizationType === 'NOT_AUTHORIZED') {
      setMessage({ type: 'error', text: 'This category indicates you are not currently authorized to work.' });
      return;
    }
    if (!unrestrictedWorkAuthorizationTypes.includes(workAuthorizationType) && !workAuthorization.expirationDate.trim()) {
      setMessage({ type: 'error', text: 'Please provide an expiration date for non-citizenship work authorization types.' });
      return;
    }

    setIsLoading(true);
    try {
      await submitWorkAuthorization(user.id, {
        capability: workAuthorizationType === 'NOT_AUTHORIZED' ? 'NO' : unrestrictedWorkAuthorizationTypes.includes(workAuthorizationType) ? 'YES' : 'CONDITIONAL',
        type: workAuthorizationType,
        workAuthorizationType,
        canWork: workAuthorizationType !== 'NOT_AUTHORIZED',
        requiresVerification: !unrestrictedWorkAuthorizationTypes.includes(workAuthorizationType),
        hasRestrictions: workAuthorizationType === 'NOT_AUTHORIZED' || manualReviewWorkAuthorizationTypes.includes(workAuthorizationType),
        expirationDate: workAuthorization.expirationDate.trim() || null,
        notes: workAuthorization.notes.trim() || null,
        idDocumentIds: workAuthorization.idDocumentIds,
      });
      await refreshProfile();
      setWorkAuthorization((prev) => ({ ...prev, status: 'pending' }));
      setMessage({ type: 'success', text: 'Work authorization submitted.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to submit work authorization.' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleSaveAll = async () => {
    if (phoneNational && digitsOnly(phoneNational).length < 7) {
      setMessage({ type: 'error', text: 'Enter a valid phone number with at least 7 digits.' });
      return;
    }
    setIsLoading(true);
    try {
      await updateProfile({
        ...profileData,
        birthDate: profileData.dateOfBirth,
        gender: profileData.gender ? profileData.gender.toUpperCase() : '',
        otherGender: profileData.gender === 'OTHER' ? profileData.otherGender : '',
        permanentAddress: {
          street: address.street,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          country: address.country,
          county: address.county,
          latitude: address.latitude,
          longitude: address.longitude,
          placeId: address.placeId,
        },
        address,
      } as any);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      refreshAuthState();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (type: 'email' | 'phone') => {
    if (type === 'phone') {
      await handleSendPhoneVerification();
      return;
    }

    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      await startEmailVerification(token);
      setMessage({ type: 'success', text: 'Verification code sent to your email!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const profilePhotoPreviewUrl = useMemo(
    () => (profilePhoto ? URL.createObjectURL(profilePhoto) : ''),
    [profilePhoto]
  );
  const securityPhotoPreviewUrl = useMemo(
    () => (securityPhoto ? URL.createObjectURL(securityPhoto) : ''),
    [securityPhoto]
  );

  useEffect(() => () => {
    if (profilePhotoPreviewUrl) URL.revokeObjectURL(profilePhotoPreviewUrl);
  }, [profilePhotoPreviewUrl]);

  useEffect(() => () => {
    if (securityPhotoPreviewUrl) URL.revokeObjectURL(securityPhotoPreviewUrl);
  }, [securityPhotoPreviewUrl]);

  const profileImageSrc =
    profilePhotoPreviewUrl ||
    displayableMediaUrl(user?.picture, user?.profilePhoto);

  const securityImageSrc =
    securityPhotoPreviewUrl ||
    displayableMediaUrl(user?.securityPhotoAt, user?.securityPhoto);
  const identityVerificationRecord = (user as any)?.userIdentityVerification || (user as any)?.identityVerification;
  const legacyIdentityDocument = (user as any)?.identityDocument;
  const isIdentityVerified = Boolean(
    user?.identityVerified ||
    identityDocument.status === 'verified' ||
    isVerificationApproved(identityVerificationRecord, legacyIdentityDocument)
  );
  const identityVerifiedOn =
    identityVerificationRecord?.verifiedOn ||
    legacyIdentityDocument?.verifiedOn ||
    '';
  const identityVerificationDecision =
    identityVerificationRecord?.verificationDecision ||
    legacyIdentityDocument?.verificationDecision ||
    (isIdentityVerified ? 'APPROVED' : '');

  return (
    <div className="profile-details-container">
      <div className="profile-details-header">
        <h1>Profile Details</h1>
        <p>Manage your personal information and account settings</p>
      </div>

      {message && (
        <div className={`profile-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-details-content">
        {/* Left Column - Account & Photos */}
        <div className="profile-left-column">
            <div className="profile-card">
              <h2>Account & Photos</h2>
            
            {/* Profile Photo */}
            <div className="profile-account-identity">
              <div className="photo-preview profile-account-avatar">
                {profileImageSrc ? (
                  <img src={profileImageSrc} alt="Profile" />
                ) : (
                  <div className="photo-placeholder">📷</div>
                )}
              </div>
              <div className="profile-account-identity-text">
                <span className="profile-account-label">Username</span>
                <strong>{profileData.username || user?.username || 'Not set'}</strong>
              </div>
            </div>

            <div className="photo-section">
              <label>PROFILE PHOTO</label>
              <div className="photo-upload-area">
                <div className="photo-preview">
                  {profileImageSrc ? (
                    <img src={profileImageSrc} alt="Profile" />
                  ) : (
                    <div className="photo-placeholder">📷</div>
                  )}
                </div>
                <button 
                  type="button"
                  className="btn-upload-photo"
                  onClick={() => openProfilePhotoPicker(profilePhotoInputRef.current)}
                  disabled={isLoading}
                >
                  {profileImageSrc ? 'Change Photo' : 'Upload Photo'}
                </button>
                <input
                  type="file"
                  ref={profilePhotoInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handlePhotoUpload('profile', e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>

            {/* Security Photo */}
            <div className="photo-section">
              <label>SECURITY PHOTO</label>
              <div className="photo-upload-area">
                <div className="photo-preview">
                  {securityImageSrc ? (
                    <img src={securityImageSrc} alt="Security" />
                  ) : (
                    <div className="photo-placeholder">🔒</div>
                  )}
                </div>
                <button 
                  type="button"
                  className="btn-upload-photo"
                  onClick={() => openProfilePhotoPicker(securityPhotoInputRef.current)}
                  disabled={isLoading}
                >
                  {securityImageSrc ? 'Change Photo' : 'Upload Photo'}
                </button>
                <input
                  type="file"
                  ref={securityPhotoInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handlePhotoUpload('security', e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input name="firstName" value={profileData.firstName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input name="lastName" value={profileData.lastName} onChange={handleInputChange} />
              </div>
            </div>

            <div className="form-group">
              <label>Middle Name</label>
              <input name="middleName" value={profileData.middleName} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label>Username</label>
              <input name="username" value={profileData.username} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={profileData.email} onChange={handleInputChange} />
              <button className="verify-link" type="button" onClick={() => handleVerification('email')} disabled={isLoading}>Verify Email</button>
            </div>
          </div>
        </div>

        {/* Right Column - Detailed Information */}
        <div className="profile-right-column">
          {/* Phone & SSN Section */}
          <div className="profile-card">
            <div className="form-group">
              <label>Phone</label>
              <div className="phone-edit-row">
                <select
                  value={phoneCallingCode}
                  onChange={(event) => handlePhonePartChange(event.target.value, phoneNational)}
                  aria-label="Calling code"
                >
                  {callingCodes.map((code) => <option key={code} value={code}>{code}</option>)}
                </select>
                <input
                  type="tel"
                  value={phoneNational}
                  onChange={(event) => handlePhonePartChange(phoneCallingCode, event.target.value)}
                  placeholder="2345678900"
                />
              </div>
              {profileData.phone && <small>Will save as: {profileData.phone}</small>}
              {profileData.phone && !user?.phoneVerified && (
                <>
                  <button className="verify-link" type="button" onClick={() => handleVerification('phone')} disabled={isLoading}>
                    {showPhoneVerification ? 'Resend Code' : 'Verify Phone'}
                  </button>
                  {showPhoneVerification && (
                    <div className="verification-input" style={{ marginTop: '0.75rem' }}>
                      <input
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={phoneVerificationCode}
                        onChange={(event) => setPhoneVerificationCode(event.target.value)}
                      />
                      <button className="btn-verify" type="button" onClick={handleVerifyPhoneCode} disabled={isLoading}>
                        Verify Phone Code
                      </button>
                    </div>
                  )}
                </>
              )}
              {user?.phoneVerified && <small className="verified-inline">Phone verified</small>}
            </div>

            <div className="form-group ssn-field-row">
              <label>SSN (Optional)</label>
              {!isEditingSSN ? (
                <>
                  <p className="ssn-display-value">{maskSSN((user as any)?.ssn)}</p>
                  <small>Used for identity verification. Stored securely.</small>
                  <button className="verify-link" type="button" onClick={() => setIsEditingSSN(true)}>
                    Edit SSN
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={formatSSN(ssnEditValue)}
                    onChange={(event) => setSsnEditValue(event.target.value)}
                    placeholder="XXX-XX-XXXX"
                    inputMode="numeric"
                    maxLength={11}
                  />
                  <div className="ssn-edit-actions">
                    <button type="button" className="verify-link" onClick={() => { setIsEditingSSN(false); setSsnEditValue(''); }}>
                      Cancel
                    </button>
                    <button type="button" className="btn-verify" onClick={saveSsn} disabled={!isValidSSN(ssnEditValue) || isLoading}>
                      Save SSN
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Bio Data - Expandable */}
          <div className="profile-card expandable">
            <div className="card-header" onClick={() => toggleSection('bioData')}>
              <h2>Bio Data</h2>
              <button className="expand-btn">
                {expandedSections.bioData ? 'View' : 'View'}
                <span className="expand-icon">{expandedSections.bioData ? '▲' : '▼'}</span>
              </button>
            </div>
            
            {expandedSections.bioData && (
              <div className="card-content">
                <div className="form-row">
                  <div className="form-group">
                    <label>DATE OF BIRTH (YYYY-MM-DD)</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={profileData.dateOfBirth}
                      onChange={handleInputChange}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <div className="form-group">
                    <label>GENDER</label>
                    <select name="gender" value={profileData.gender} onChange={handleInputChange}>
                      <option value="">Not set</option>
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                {profileData.gender === 'OTHER' && (
                  <div className="form-group">
                    <label>Specify Gender</label>
                    <input name="otherGender" value={profileData.otherGender} onChange={handleInputChange} />
                  </div>
                )}
                <div className="form-group">
                  <label>Status</label>
                  <p className="status-badge">{profileData.status}</p>
                </div>
              </div>
            )}
          </div>

          {/* Address - Expandable */}
          <div className="profile-card expandable">
            <div className="card-header" onClick={() => toggleSection('address')}>
              <h2>Address</h2>
              <button className="expand-btn">
                {expandedSections.address ? 'View' : 'View'}
                <span className="expand-icon">{expandedSections.address ? '▲' : '▼'}</span>
              </button>
            </div>
            
            {expandedSections.address && (
              <div className="card-content">
                <div className="search-address">
                  <GooglePlacesAutocompleteInput
                    googleMapsApiKey={googleMapsApiKey}
                    placeholder="Search Address"
                    value={addressSearch}
                    onChange={setAddressSearch}
                    onResolvePlace={handleAddressSelected}
                  />
                </div>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    name="street"
                    value={address.street}
                    onChange={handleAddressFieldChange}
                    placeholder="Street address"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={address.city}
                      onChange={handleAddressFieldChange}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={address.state}
                      onChange={handleAddressFieldChange}
                      placeholder="State"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>ZIP</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={address.zipCode}
                      onChange={handleAddressFieldChange}
                      placeholder="ZIP Code"
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input type="text" name="country" value={address.country} onChange={handleAddressFieldChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="text" name="latitude" value={address.latitude} readOnly />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="text" name="longitude" value={address.longitude} readOnly />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Identity Verification - Expandable */}
          <div className="profile-card expandable">
            <div className="card-header" onClick={() => toggleSection('identityVerification')}>
              <h2>Identity Verification</h2>
              <button className="expand-btn">
                {expandedSections.identityVerification ? 'Hide' : 'View'}
                <span className="expand-icon">{expandedSections.identityVerification ? '▲' : '▼'}</span>
              </button>
            </div>
            
            {expandedSections.identityVerification && (
              <div className="card-content">
                <div className="verification-status-badge">
                  {isIdentityVerified ? (
                    <span className="verified">✓ Verified</span>
                  ) : (
                    <span className="not-verified">Not Verified</span>
                  )}
                </div>
                
                {isIdentityVerified ? (
                  <div className="document-info">
                    <div className="document-details">
                      <p><strong>Document Type:</strong> {identityDocument.documentType || 'Verified document'}</p>
                      <p><strong>Status:</strong> Verified</p>
                      {identityVerificationDecision && (
                        <p><strong>Decision:</strong> {identityVerificationDecision.replace(/_/g, ' ')}</p>
                      )}
                      {identityVerifiedOn && (
                        <p><strong>Verified On:</strong> {formatDateTime(identityVerifiedOn)}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="verification-text">
                      Upload a valid ID document so we can verify your identity.
                    </p>

                    <div className="document-upload-section">
                      <label>IDENTITY VERIFICATION DOCUMENT</label>
                      <p className="verification-text">Next upload: <strong>{identityNextPageLabel()}</strong></p>

                      <div className="document-upload-area">
                        <div className="document-options">
                          <div className="document-type-select">
                            <label>Document Type</label>
                            <select
                              value={identityDocument.documentType}
                              onChange={(e) => setIdentityDocument({ ...identityDocument, documentType: e.target.value })}
                            >
                              <option value="">Select Document Type</option>
                              <option value="PASSPORT">Passport</option>
                              <option value="DRIVERS_LICENSE">Driver's License</option>
                              <option value="GOVERNMENT_ID">Government ID</option>
                            </select>
                            <p className="recommendation">Recommended: Use Driver's License for instant and more reliable verification.</p>
                          </div>

                          <div className="upload-options">
                            <button
                              type="button"
                              className="btn-gallery"
                              onClick={openIdentityDocumentPicker}
                              disabled={isLoading}
                            >
                              📷 GALLERY
                              <span>Add {identityNextPageLabel()}</span>
                            </button>
                            <button
                              type="button"
                              className="btn-camera"
                              onClick={openIdentityDocumentCamera}
                              disabled={isLoading}
                            >
                              📸 CAMERA
                              <span>Capture {identityNextPageLabel()}</span>
                            </button>
                          </div>
                        </div>
                        <input
                          type="file"
                          ref={identityDocumentInputRef}
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length) {
                              handleIdentityDocumentUpload(files);
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>

                      <input
                        type="file"
                        ref={identityDocumentReplaceInputRef}
                        accept="image/*,.pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length) {
                            handleIdentityDocumentUpload(files, identityReplaceIndexRef.current ?? identityReplaceIndex);
                            e.target.value = '';
                          }
                        }}
                      />

                      {identityDocuments.length > 0 && (
                        <div className="document-info">
                          <div className="document-details">
                            <p><strong>Document Type:</strong> {identityDocument.documentType || 'Not selected'}</p>
                            <p><strong>Status:</strong> {identityDocument.status === 'pending' ? 'Pending Verification' : identityDocument.status}</p>
                          </div>
                          <div className="document-review-grid">
                            {identityDocuments.map((document, index) => (
                              <div className="document-review-card" key={`${document.fileId}-${index}`}>
                                <div className="document-review-preview">
                                  {document.previewUrl ? (
                                    <img src={document.previewUrl} alt={documentPageLabel(index)} />
                                  ) : (
                                    <span>{document.mimeType?.includes('pdf') ? 'PDF' : 'File'}</span>
                                  )}
                                </div>
                                <div className="document-review-meta">
                                  <strong>{documentPageLabel(index)}</strong>
                                  <span>{document.name}</span>
                                  {document.uploadedAt && <small>{document.uploadedAt}</small>}
                                  <small>Uploaded</small>
                                </div>
                                <div className="document-review-actions">
                                  <button
                                    type="button"
                                    onClick={() => openIdentityDocumentReplacePicker(index)}
                                    disabled={isLoading}
                                  >
                                    Replace
                                  </button>
                                  <button type="button" onClick={() => removeIdentityDocument(index)}>Remove</button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <button 
                            type="button"
                            className="btn-update-document"
                            onClick={openIdentityDocumentPicker}
                            disabled={isLoading}
                          >
                            Add More Documents
                          </button>
                        </div>
                      )}
                    </div>

                    {identityDocument.documentType && (
                      <button 
                        type="button"
                        className="btn-submit-verification"
                        onClick={handleSubmitIdentityVerification}
                        disabled={isLoading}
                      >
                        Submit Verification
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="profile-card expandable">
            <div className="card-header" onClick={() => toggleSection('workAuthorization')}>
              <h2>Work Authorization</h2>
              <button className="expand-btn">
                {expandedSections.workAuthorization ? 'Hide' : 'View'}
                <span className="expand-icon">{expandedSections.workAuthorization ? '▲' : '▼'}</span>
              </button>
            </div>

            {expandedSections.workAuthorization && (
              <div className="card-content">
                <div className="form-group">
                  <label>Work Authorization Type</label>
                  <select
                    value={workAuthorization.workAuthorizationType}
                    onChange={(e) => setWorkAuthorization((prev) => ({ ...prev, workAuthorizationType: e.target.value }))}
                  >
                    <option value="">Select Work Authorization Type</option>
                    <option value="US_CITIZEN">US Citizen</option>
                    <option value="PERMANENT_RESIDENT">Permanent Resident</option>
                    <option value="EMPLOYMENT_AUTHORIZATION_DOCUMENT">Employment Authorization Document</option>
                    <option value="ASYLUM">Asylum</option>
                    <option value="REFUGEE">Refugee</option>
                    <option value="TEMPORARY_PROTECTED_STATUS">Temporary Protected Status</option>
                    <option value="DACA">DACA</option>
                    <option value="INTERNATIONAL_STUDENT">International Student</option>
                    <option value="WORK_VISA">Work Visa</option>
                    <option value="DEPENDENT_VISA_WITH_WORK_AUTH">Dependent Visa With Work Auth</option>
                    <option value="NOT_AUTHORIZED">Not Authorized</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    value={workAuthorization.expirationDate}
                    onChange={(e) => setWorkAuthorization((prev) => ({ ...prev, expirationDate: e.target.value }))}
                    disabled={unrestrictedWorkAuthorizationTypes.includes(workAuthorization.workAuthorizationType)}
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    value={workAuthorization.notes}
                    onChange={(e) => setWorkAuthorization((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </div>

                <label className="tool-inline" style={{ justifyContent: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={workAuthorization.attestedAuthorized}
                    onChange={(e) => setWorkAuthorization((prev) => ({ ...prev, attestedAuthorized: e.target.checked }))}
                  />
                  I attest that I am authorized to perform work through Olitask and that the information provided is accurate.
                </label>

                <div className="verification-review">
                  <p><strong>Review:</strong> {workAuthorization.workAuthorizationType || 'No type selected'}</p>
                  <p><strong>Capability:</strong> {workAuthorization.workAuthorizationType === 'NOT_AUTHORIZED' ? 'No' : unrestrictedWorkAuthorizationTypes.includes(workAuthorization.workAuthorizationType) ? 'Yes' : 'Conditional'}</p>
                  <p><strong>Documents:</strong> {unrestrictedWorkAuthorizationTypes.includes(workAuthorization.workAuthorizationType) ? 'Optional' : 'Optional but recommended for review'}</p>
                </div>

                <button
                  type="button"
                  className="btn-update-document"
                  onClick={openWorkAuthorizationPicker}
                  disabled={isLoading}
                >
                  Upload Documents
                </button>
                <input
                  type="file"
                  ref={workAuthorizationInputRef}
                  accept="image/*,.pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      handleWorkAuthorizationUpload(files);
                      e.target.value = '';
                    }
                  }}
                />
                <input
                  type="file"
                  ref={workAuthorizationReplaceInputRef}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      handleWorkAuthorizationUpload(files, workReplaceIndexRef.current ?? workReplaceIndex);
                      e.target.value = '';
                    }
                  }}
                />
                <p><strong>Status:</strong> {workAuthorization.status}</p>
                {workAuthorizationDocuments.length > 0 && (
                  <div className="document-review-grid">
                    {workAuthorizationDocuments.map((document, index) => (
                      <div className="document-review-card" key={`${document.fileId}-${index}`}>
                        <div className="document-review-preview">
                          {document.previewUrl ? (
                            <img src={document.previewUrl} alt={documentPageLabel(index)} />
                          ) : (
                            <span>{document.mimeType?.includes('pdf') ? 'PDF' : 'File'}</span>
                          )}
                        </div>
                        <div className="document-review-meta">
                          <strong>{documentPageLabel(index)}</strong>
                          <span>{document.name}</span>
                          {document.uploadedAt && <small>{document.uploadedAt}</small>}
                          <small>Uploaded</small>
                        </div>
                        <div className="document-review-actions">
                          <button
                            type="button"
                            onClick={() => openWorkAuthorizationReplacePicker(index)}
                            disabled={isLoading}
                          >
                            Replace
                          </button>
                          <button type="button" onClick={() => removeWorkAuthorizationDocument(index)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className="btn-submit-verification"
                  onClick={handleSubmitWorkAuthorization}
                  disabled={isLoading}
                >
                  Submit Work Authorization
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isIdentityCameraOpen && (
        <div className="identity-camera-overlay" role="dialog" aria-modal="true" aria-label="Capture identity document">
          <div className="identity-camera-panel">
            <div className="identity-camera-header">
              <h3>Capture {identityNextPageLabel()}</h3>
              <button type="button" onClick={stopIdentityDocumentCamera} aria-label="Close camera">
                x
              </button>
            </div>
            <div className="identity-camera-preview">
              {identityCameraStream ? (
                <video ref={identityCameraVideoRef} autoPlay muted playsInline />
              ) : (
                <div className="identity-camera-empty">
                  {identityCameraError || 'Starting camera...'}
                </div>
              )}
            </div>
            {identityCameraError && <p className="identity-camera-error">{identityCameraError}</p>}
            <div className="identity-camera-actions">
              <button type="button" onClick={stopIdentityDocumentCamera}>
                Cancel
              </button>
              <button
                type="button"
                className="identity-camera-capture"
                onClick={captureIdentityDocumentPhoto}
                disabled={!identityCameraStream || isLoading}
              >
                Capture Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="profile-actions">
        <button className="btn-save-all" onClick={handleSaveAll} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
};

export default ProfileDetails;
